import { AxiosResponse } from 'axios';
import {
  takeEvery, put, fork, select,
} from 'redux-saga/effects';
import { postAnswerForm } from 'services/fieldNotebookService';
import { verifyRules } from 'utils/fieldNotebookUtils/verifyRules';
import { isSerializedImageAnswer } from 'utils/fieldNotebookUtils/imageAnswer';
import { TrackingService } from 'services/trackingService';
import { showSnackbar } from '../snackbar/actions';
import { getArchivesRequest } from '../archives/actions';
import {
  clearAnswersWhenEditAConditionalAnswer,
  nextStepSuccess,
  sendFormError,
  sendFormSuccess,
  setEditParams,
} from './actions';
import { Answer, Field, FormsActionTypes } from './types';
import {
  clearAnswersToNull,
  toRequestAnswerValue,
  verifyNeedsCleanConditionalAnswersWhenEditing,
} from './utils';

/**
 * Monta o `answers` do POST /field_notebook/form_answers a partir do estado:
 * campos de imagem viram array de signed_id/URL, subfields e checkbox viram
 * array, os demais seguem como estão.
 */
export function buildCreateAnswersPayload(answers: Answer[], fields: Field[]) {
  const typeByFieldId = new Map(fields.map((field) => [field.field_id, field.type]));

  return answers.map((answer: Answer) => {
    const componentType = typeByFieldId.get(answer.field_id);
    const isImage =
      componentType === 'image' ||
      (componentType === undefined && isSerializedImageAnswer(answer.value));

    if (isImage) {
      return { ...answer, value: toRequestAnswerValue(answer.value, 'image') };
    }
    if (typeof answer.value === 'string' && answer.value.includes('sub_fields')) {
      return {
        ...answer,
        value: JSON.parse(answer.value),
      };
    }
    if (typeof answer.value === 'string' && answer.value.includes('\t')) {
      return {
        ...answer,
        value: answer.value.split('\t').slice(1),
      };
    }
    return answer;
  });
}

function* sendAnswers(action: any) {
  const { successMessage, errorMessage } = action.payload;
  try {
    const { jwt } = yield select((state) => state.user);
    const { answers } = yield select((state) => state.forms.fillData);
    const { fields } = yield select((state) => state.forms.selectedForm);
    const { externalId } = yield select((state) => state.organization);
    const data = {
      response: {
        answers,
      },
    };

    data.response.answers = buildCreateAnswersPayload(answers, fields ?? []);

    const response: AxiosResponse = yield postAnswerForm({
      jwt,
      data,
      externalId,
    });

    if (response.status >= 200 && response.status < 300) {
      TrackingService.sendEvent('fieldnotebook_annotation_created');
      yield put(sendFormSuccess());
      yield put(getArchivesRequest());
      yield put(
        showSnackbar({ message: successMessage, typeMessage: 'success' }),
      );
    } else {
      TrackingService.sendEvent('fieldnotebook_annotation_create_error');
      yield put(sendFormError());
      yield put(showSnackbar({ message: errorMessage, typeMessage: 'error' }));
    }
  } catch (error) {
    TrackingService.sendEvent('fieldnotebook_annotation_create_error');
    yield put(sendFormError());
    yield put(showSnackbar({ message: errorMessage, typeMessage: 'error' }));
  }
}

function* verifyNextStep(action: any) {
  try {
    const { answer } = action.payload;
    const { forms } = yield select((state) => state);
    const { step, totalSteps, answers } = forms.fillData;
    const { fields } = forms.selectedForm;
    let currentAnswers : Answer[] = [...answers];
    const answerIndex = currentAnswers.findIndex((ans: { field_id: any; }) => ans.field_id === answer.field_id);
    const isNeedClearAnswers = verifyNeedsCleanConditionalAnswersWhenEditing(answer, currentAnswers, fields);

    if (isNeedClearAnswers) {
      yield put(clearAnswersWhenEditAConditionalAnswer(answerIndex));
      currentAnswers = clearAnswersToNull(currentAnswers, answerIndex);
    }
    currentAnswers[answerIndex] = answer;

    const newAnswers = answer ? [...currentAnswers, answer] : currentAnswers;
    let nextFieldIndex = step + 1;

    while (nextFieldIndex < totalSteps) {
      const nextField = fields[nextFieldIndex];

      if (!verifyRules(nextField, newAnswers)) {
        nextFieldIndex += 1;
      } else {
        break;
      }
    }
    if (nextFieldIndex <= totalSteps) {
      yield put(nextStepSuccess(answer, nextFieldIndex, answerIndex, false));
    } else {
      nextFieldIndex = totalSteps - 1;
      yield put(nextStepSuccess(answer, nextFieldIndex, answerIndex, true));
    }
  } catch (err) {
    console.error(err);
  }
}

function* editAnswerSettings(action: any) {
  try {
    const { field } = action.payload;
    const { forms } = yield select((state) => state);
    const { answers } = forms.fillData;
    const { fields } = forms.selectedForm;
    const fieldStack: number[] = [];
    const editingFieldIndex = fields.findIndex((searchField: Field) => searchField.field_id === field.field_id);

    answers.forEach((answer: Answer) => {
      const fieldIndex = fields.findIndex((searchField: Field) => searchField.field_id === answer.field_id);
      if (editingFieldIndex >= fieldIndex) {
        fieldStack.push(fieldIndex);
      }
    });

    yield put(setEditParams(editingFieldIndex, fieldStack));
  } catch (err) {
    console.error(err);
  }
}
function* watchGetArchivesRequest() {
  yield takeEvery(FormsActionTypes.SEND_FORM_REQUEST, sendAnswers);
}

function* watchNextStep() {
  yield takeEvery(FormsActionTypes.NEXT_STEP, verifyNextStep);
}

function* watchEditAnswer() {
  yield takeEvery(FormsActionTypes.EDIT_ANSWER, editAnswerSettings);
}

export const formSagas = [fork(watchGetArchivesRequest), fork(watchNextStep), fork(watchEditAnswer)];
